import { Button } from '@tempo-io/sui-button';
import { Close } from '@tempo-io/sui-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemLinkValue } from '@/common/api/types/items';
import { EmptyFieldValue } from '@/features/items/components/item-field-value/EmptyFieldValue';
import { Field } from '@/features/items/components/item-field-value/Field';
import { FieldWithStatusIndicator } from '@/features/items/components/item-field-value/FieldWithStatusIndicator';
import { LinkButton } from './LinkButton';
import { LinkEditDialog } from './LinkEditDialog';

export const LinkField = ({
  id,
  name,
  value,
  isEditable = false,
  autoFocus = false,
  onChange,
  isPending,
  isSuccess,
  isError,
  error,
}: {
  id: string;
  name: string;
  value?: ItemLinkValue;
  isEditable?: boolean;
  autoFocus?: boolean;
  onChange?: (value: ItemLinkValue | undefined) => void;
  isPending?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: Error | null;
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(autoFocus && !value?.LINK);
  const inputId = `link-field-${id}`;

  const handleSave = (linkValue: { url: string; description?: string }) => {
    onChange?.({
      LINK: linkValue.url,
      description: linkValue.description,
    });
    setDialogOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange?.(undefined);
  };

  if (!isEditable) {
    return (
      <Field inputId={inputId} name={name}>
        {value?.LINK ? (
          <LinkButton url={value.LINK} description={value.description} id={inputId} />
        ) : (
          <EmptyFieldValue />
        )}
      </Field>
    );
  }

  return (
    <FieldWithStatusIndicator
      inputId={inputId}
      name={name}
      isPending={isPending}
      isSuccess={isSuccess}
    >
      <div className="flex flex-col gap-1">
        {value?.LINK ? (
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <LinkButton
                url={value.LINK}
                description={value.description}
                onClick={isPending ? undefined : () => setDialogOpen(true)}
                isEditable
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              aria-label={t(($) => $.adaptivePlanner.itemPanel.linkField.clear)}
              className="h-8 w-8 p-0"
              disabled={isPending}
            >
              <Close />
            </Button>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="justify-start text-text-neutral-secondary"
            onClick={() => setDialogOpen(true)}
            disabled={isPending}
          >
            {t(($) => $.adaptivePlanner.itemPanel.customFields.multiValue.addValue)}
          </Button>
        )}
        {isError && error && <span className="text-xs text-text-error">{error.message}</span>}
      </div>

      <LinkEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialValue={value ? { url: value.LINK, description: value.description } : undefined}
        onSave={handleSave}
        mode={value ? 'edit' : 'add'}
      />
    </FieldWithStatusIndicator>
  );
};
